import type {
  FieldSnapshot,
  PricingBreakdownLine,
  PricingField,
  PricingItem,
  PricingResult,
  PricingSelection,
} from '~/types/servicePackage'

function roundToThousand(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.round(value / 1000) * 1000
}

function selectedOptions(field: PricingField, selection?: PricingSelection) {
  const ids = new Set(selection?.optionIds ?? [])
  return [...field.options]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter(option => ids.has(option.id))
}

export function applyDefaultSelections(fields: PricingField[], selections: PricingSelection[]): PricingSelection[] {
  const byFieldId = new Map(selections.map(item => [item.fieldId, item]))
  return fields.map((field) => {
    const current = byFieldId.get(field.id)
    if (field.inputType === 'number') {
      if (current?.value != null && Number.isFinite(Number(current.value))) return current
      return { fieldId: field.id, value: field.min ?? 0 }
    }
    if (current?.optionIds?.length) return current
    return {
      fieldId: field.id,
      optionIds: field.options.filter(option => option.isDefault).map(option => option.id),
    }
  })
}

export function missingRequiredFields(fields: PricingField[], selections: PricingSelection[]): string[] {
  const resolved = applyDefaultSelections(fields, selections)
  const byFieldId = new Map(resolved.map(item => [item.fieldId, item]))
  return fields.filter((field) => {
    if (!field.isRequired) return false
    const selection = byFieldId.get(field.id)
    if (field.inputType === 'number') return selection?.value == null || !Number.isFinite(Number(selection.value))
    return !selection?.optionIds?.length
  }).map(field => field.label)
}

export function computePackagePrice(input: {
  basePrice: number
  items: PricingItem[]
  fields: PricingField[]
  selections: PricingSelection[]
}): PricingResult {
  const breakdown: PricingBreakdownLine[] = []
  const itemDelta = input.items.reduce((sum, item) => sum + (item.priceDelta || 0), 0)
  const base = (input.basePrice || 0) + itemDelta
  breakdown.push({ kind: 'base', label: 'قیمت پایه', amount: input.basePrice || 0 })
  if (itemDelta) breakdown.push({ kind: 'item', label: 'تفاوت خدمات پکیج', amount: itemDelta })

  const selections = applyDefaultSelections(input.fields, input.selections)
  const byFieldId = new Map(selections.map(item => [item.fieldId, item]))
  const fields = [...input.fields].sort((a, b) => a.sortOrder - b.sortOrder)

  let add = 0
  let sub = 0
  let numberPart = 0
  let setValue: number | null = null
  let sessionDelta = 0
  const percents: number[] = []
  const snapshots: FieldSnapshot[] = []

  for (const field of fields) {
    const selection = byFieldId.get(field.id)
    if (field.inputType === 'number') {
      const min = field.min ?? 0
      const max = field.max ?? Number.POSITIVE_INFINITY
      const raw = Number(selection?.value ?? min)
      const qty = Number.isFinite(raw) ? Math.min(max, Math.max(min, raw)) : min
      const unit = field.unitPrice ?? 0
      const amount = qty * unit
      numberPart += amount
      if (amount) breakdown.push({ kind: 'number', label: `${field.label}${field.unitLabel ? ` (${field.unitLabel})` : ''}`, amount })
      snapshots.push({
        fieldId: field.id,
        label: field.label,
        inputType: 'number',
        value: qty,
        priceEffect: amount,
        sessionDelta: 0,
      })
      continue
    }

    const options = selectedOptions(field, selection)
    let effect = 0
    let optionSessionDelta = 0
    for (const option of options) {
      const value = Number(option.priceValue) || 0
      optionSessionDelta += option.sessionDelta || 0
      if (option.priceOp === 'add') {
        add += value
        effect += value
        breakdown.push({ kind: 'add', label: `${field.label}: ${option.label}`, amount: value })
      }
      else if (option.priceOp === 'sub') {
        sub += value
        effect -= value
        breakdown.push({ kind: 'sub', label: `${field.label}: ${option.label}`, amount: -value })
      }
      else if (option.priceOp === 'set') {
        setValue = value
        effect = value
        breakdown.push({ kind: 'set', label: `${field.label}: ${option.label}`, amount: value })
      }
      else if (option.priceOp === 'percent') {
        percents.push(value)
        breakdown.push({ kind: 'percent', label: `${field.label}: ${option.label}`, amount: value })
      }
    }
    sessionDelta += optionSessionDelta
    snapshots.push({
      fieldId: field.id,
      label: field.label,
      inputType: field.inputType,
      optionLabels: options.map(option => option.label),
      priceEffect: effect,
      sessionDelta: optionSessionDelta,
    })
  }

  const fixed = base + add - sub + numberPart
  const source = setValue ?? fixed
  const percentMultiplier = percents.reduce((product, value) => product * (1 + value / 100), 1)
  const final = Math.max(0, roundToThousand(source * percentMultiplier))
  const sessions = Math.max(1, input.items.reduce((sum, item) => sum + Math.max(0, item.sessionCount || 0), 0) + sessionDelta)

  return { base, fixed, percentMultiplier, final, sessions, breakdown, fieldSnapshots: snapshots }
}

export function fieldSnapshotsFromPrice(input: {
  fields: PricingField[]
  selections: PricingSelection[]
}): FieldSnapshot[] {
  return computePackagePrice({
    basePrice: 0,
    items: [],
    fields: input.fields,
    selections: input.selections,
  }).fieldSnapshots
}
